import React, { useEffect, useState } from "react";
import { CircularProgress, Grid, VeBox, VeTypography } from "../../../mui";
import { CommonUpload } from "../../commonUpload/CommonUpload";
import InfoImg from "../OnboardingPage/InfoImg";
import { VeTooltip } from "../../VeTooltip/VeTooltip";
import { DocumentCard } from "./DocumentCard";

const Step2 = ({
  assets,
  DynamicImage,
  fileTypeImages,
  useSession,
  mutateAction,
  submitCurrentStep,
  setSubmitCurrentStep,
  setSelectedTab,
  variables,
  setIsLoading,
  projectDetails,
  setProjectDetails,
  applicationRefetch,
  financingRefetch,
}: any) => {
  const [fileSelected, setFileSelected] = useState<any[]>([]);
  const [fileUploaded, setFileUploaded] = useState<any[]>([]);
  const { data: session, update } = useSession();

  const [fileKey, setFileKey] = useState({
    key: "",
    actionType: "",
  });

  const {
    data: documentsData,
    refetch,
    isFetching,
  } = variables.useQueryData({
    queryKey: ["documents-list", projectDetails?.id],
    endPoint: `/project/description/user/docs?id=${projectDetails?.id}&limit=100000&offset=0`,
    // staleTime: 1000 * 60 * 5, // 5 minutes
    // cacheTime: 1000 * 60 * 10, // Cache data for 10 minutes
  });

  const { mutate: updateProject } = variables.useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-project"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      setProjectDetails(data?.updated_project_data);
      projectDetails && variables?.projectRefetch();
    },
    onError: (error: any) => {
      variables?.toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate, isPending } = variables.useMutation({
    mutationFn: mutateAction,
    mutationKey: ["create-project-document"],
    onSuccess: async (response: any) => {
      if (response.error)
        throw new Error(response.error?.heading, {
          cause: response?.error?.message,
        });
      variables.toast(`Document added successfully!`, { type: "success" });
      refetch();
      variables?.documentsRefetch();
      // setSelectedTab(2)
      updateProject({
        type: "patch",
        endPoint: `/project/v2/onboarding/${projectDetails?.id}`,
        data: {
          stepper: {
            ...projectDetails?.stepper,
            documents: "COMPLETED",
          },
        },
        session,
        update,
      });
      setIsLoading(false);
      setFileSelected([]);
    },
    onError: (error: any) => {
      setIsLoading(false);
      variables.toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate: deleteFileMutate, isPending: deletePending } =
    variables.useMutation({
      mutationKey: ["Delete-file"],
      mutationFn: mutateAction,
      onSuccess: (data: any) => {
        if (data.error)
          throw new Error(data.error?.heading, { cause: data?.error?.message });
        setFileKey({ actionType: "", key: "" });
        if (fileUploaded?.length === 1) {
          setProjectDetails({
            ...projectDetails,
            stepper: {
              ...projectDetails?.stepper,
              documents: "NOT_STARTED",
            },
          });
          projectDetails && variables?.projectRefetch();
        }
        refetch();
        // applicationRefetch()
        // financingRefetch()
        variables?.documentsRefetch();
      },
      onError: (error: any) => {
        variables.toast(error?.cause as string, { type: "error" });
      },
    });

  useEffect(() => {
    if (documentsData?.projectDocs) {
      setFileUploaded(
        documentsData?.projectDocs?.map((item: any) => {
          return {
            title: item?.displayName,
            type: item?.type,
            date: item?.dateOfDocumentCreated,
            fileSize: item?.fileSize,
            key: item?.id,
            fileKey: item?.key,
          };
        })
      );
    }
  }, [documentsData]);

  useEffect(() => {
    fileSelected?.length > 0 && uploadToProjectDocuments();
  }, [fileSelected]);

  useEffect(() => {
    if (fileKey.actionType === "DELETE") {
      deleteFileMutate({
        endPoint: `/project/description/drive`,
        type: "post",
        data: {
          id: [fileKey.key],
        },
        session,
        update,
      });
    }
  }, [fileKey]);

  function formatFileSize(sizeInBytes: number) {
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} Bytes`;
    } else if (sizeInBytes < 1048576) {
      return `${(sizeInBytes / 1024).toFixed(2)} KB`;
    } else if (sizeInBytes < 1073741824) {
      return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
    } else {
      return `${(sizeInBytes / 1073741824).toFixed(2)} GB`;
    }
  }

  const uploadToProjectDocuments = () => {
    const dataPayload = fileSelected?.map((item: any) => {
      return {
        projectId: projectDetails?.id || "6f8247e0-227b-49e5-a3e2-103bfd4edcfd",
        displayName: item?.originalFileName,
        key: item?.s3ObjectKey,
        dateOfDocumentCreated: new Date().toISOString(),
        fileSize: item?.fileSize?.toString(),
      };
    });
    mutate({
      type: "post",
      endPoint: "/project/description/bulk-docs",
      data: {
        document: dataPayload,
      },
      session,
      update,
    });
  };

  const onRemove = (item: any) => {
    // alert("DELETES FILE");
    setFileKey({
      actionType: "DELETE",
      key: item,
    });
  };

  return (
    <VeBox>
      <VeBox display={"flex"} flexDirection={"column"}>
        <VeTypography variant="subtitle1Main" display={"flex"}>
          Project Documents
          <VeTooltip
            placement="right"
            title={
              <VeBox
                display={"flex"}
                width={"340px"}
                flexDirection={"column"}
                padding={"8px"}
              >
                <VeTypography
                  variant="label4Main"
                  sx={{ color: "white !important", fontSize: "12px" }}
                >
                  Please ensure that the files are uploaded in one of the
                  following formats: .avi, .mov, .webm, .wmv, .mpeg, .gp, .mkv,
                  .mp4, .flv, .kml, .kmz, .dbf, .shp, .shx, .tif, .jpg, .tiff,
                  .webp, .png, .jpeg, .gif, .bmp, .svg, .heic, .pdf, .xlsm,
                  .csv, .doc, .docx, .md, .ods, .odt, .potx, .pps, .ppsx, .ppt,
                  .rtf, .xls, .xlsx, .xtl, .html, .odp, .txt, .xml, .json,
                  .pptx.
                </VeTypography>
              </VeBox>
            }
            arrow
            sx={{ display: "flex", justifyContent: "center" }}
            // color="white"
          >
            <VeBox
              display={"flex"}
              sx={{ cursor: "help" }}
              width={"fit-content"}
              alignItems={"center"}
              marginLeft={"5px"}
            >
              <InfoImg />
            </VeBox>
          </VeTooltip>
        </VeTypography>
        <VeTypography
          variant="body2Tertiary"
          marginTop={"16px"}
          marginBottom={"32px"}
        >
          Here’s an overview of the documents you can upload to support your
          project: Pre-Design draft, Project Presentation, Financial Models and
          Plans, Project update Reports, Data Collection Plans, Risk Assessment
          Reports.
        </VeTypography>
      </VeBox>
      <CommonUpload
        noMargin={true}
        admin={true}
        edit={true}
        uploadFiles={mutateAction}
        useSession={useSession}
        displayFiles={false}
        title={""}
        icon={DynamicImage(assets?.uploadVariant, "close", 44, 44)}
        filesSelected={fileSelected}
        setFileSelected={setFileSelected}
        bucketType={process.env.NEXT_PUBLIC_BUCKET_FILES!}
        required={true}
        image={DynamicImage}
        noPadding
        fileTypeImages={fileTypeImages}
        acceptedTypes={[
          // Videos
          "video/x-msvideo", // .avi
          "video/quicktime", // .mov
          "video/webm", // .webm
          "video/x-ms-wmv", // .wmv
          "video/mpeg", // .mpeg
          "video/3gpp", // .gp
          "video/x-matroska", // .mkv
          "video/mp4", // .mp4
          "video/x-flv", // .flv

          // Location
          "application/vnd.google-earth.kml+xml", // .kml
          "application/vnd.google-earth.kmz", // .kmz
          "application/x-dbf", // .dbf
          "application/octet-stream", // .shp
          "application/octet-stream", // .shx

          // Gallery
          "image/tiff", // .tif, .tiff
          "image/jpeg", // .jpg, .jpeg
          "image/webp", // .webp
          "image/png", // .png
          "image/gif", // .gif
          "image/bmp", // .bmp
          "image/svg+xml", // .svg
          "image/heic", // .heic

          // Document
          "application/pdf", // .pdf
          "application/vnd.ms-excel.sheet.macroEnabled.12", // .xlsm
          "text/csv", // .csv
          "application/msword", // .doc
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
          "text/markdown", // .md
          "application/vnd.oasis.opendocument.spreadsheet", // .ods
          "application/vnd.oasis.opendocument.text", // .odt
          "application/vnd.openxmlformats-officedocument.presentationml.template", // .potx
          "application/vnd.ms-powerpoint", // .pps
          "application/vnd.openxmlformats-officedocument.presentationml.slideshow", // .ppsx
          "application/vnd.ms-powerpoint", // .ppt
          "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
          "application/rtf", // .rtf
          "application/vnd.ms-excel", // .xls
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xtl
          "text/html", // .html
          "application/vnd.oasis.opendocument.presentation", // .odp
          "text/plain", // .txt
          "application/xml", // .xml
          "text/xml",
          "application/json", // .json
        ]}
        kmlFileType={true}
        mutliUpload={true}
        setFileKey={setFileKey}
      />
      <VeBox display={"flex"} flexDirection={"column"} marginTop={"-30px"}>
        <VeTypography
          variant="body1StrongMain"
          borderBottom={"1px solid #E8E8E8"}
          paddingBottom={"8px"}
        >
          Uploaded documents
        </VeTypography>
        {fileUploaded?.length === 0 ? (
          isFetching ? (
            <VeBox
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"200px"}
            >
              <CircularProgress
                sx={{
                  color: "var(--vc-violet)",
                }}
              />
            </VeBox>
          ) : (
            <VeBox
              display={"flex"}
              marginBottom={"50px"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              paddingTop={"24px"}
            >
              {DynamicImage(assets?.file, "file", 80, 80)}
              <VeTypography
                marginTop={"12px"}
                variant="label2StrongerSecondary"
              >
                No project files
              </VeTypography>
              <VeTypography
                marginTop={"12px"}
                textAlign={"center"}
                variant="label3Secondary"
              >
                There are no files uploaded for this project. Upload files to
                populate this list.
              </VeTypography>
            </VeBox>
          )
        ) : isFetching ? (
          <VeBox
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"200px"}
          >
            <CircularProgress
              sx={{
                color: "var(--vc-violet)",
              }}
            />
          </VeBox>
        ) : (
          <Grid container marginTop={"16px"} marginBottom={"50px"}>
            {fileUploaded?.map((item: any, mainIndex: number) => (
              <DocumentCard
                onRemove={onRemove}
                DynamicImage={DynamicImage}
                assets={assets}
                variables={variables}
                item={item}
                formatFileSize={formatFileSize}
                loading={deletePending}
                fileKey={fileKey}
                mainIndex={fileUploaded?.length === mainIndex + 1}
              />
            ))}
          </Grid>
        )}
      </VeBox>
    </VeBox>
  );
};

export default Step2;
